import React, { useState, useMemo, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const CollectionReports = () => {
  const [collections, setCollections] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: {
      start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0]
    },
    fosFilter: '',
    retailerFilter: '',
    paymentMethod: '',
    collectionType: '',
    amountRange: { min: '', max: '' },
    reportType: 'detailed'
  });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [reportData, setReportData] = useState(null);

  const mockCollections = [
    {
      id: 'COL001',
      date: '2024-01-15',
      fosId: 'FOS001',
      fosName: 'Vikram Singh',
      retailerId: 'RET001',
      retailerName: 'Smart Mobile Store',
      amount: 25000,
      paymentMethod: 'UPI',
      referenceNumber: 'TXN123456',
      collectionType: 'Outstanding Payment',
      collectedBy: 'Vikram Singh',
      commission: 625,
      status: 'Completed'
    },
    {
      id: 'COL002',
      date: '2024-01-14',
      fosId: 'FOS002',
      fosName: 'Anita Desai',
      retailerId: 'RET003',
      retailerName: 'Digital World',
      amount: 18000,
      paymentMethod: 'Bank Transfer',
      referenceNumber: 'TXN123457',
      collectionType: 'Outstanding Payment',
      collectedBy: 'Anita Desai',
      commission: 360,
      status: 'Completed'
    },
    {
      id: 'COL003',
      date: '2024-01-13',
      fosId: 'FOS001',
      fosName: 'Vikram Singh',
      retailerId: 'RET002',
      retailerName: 'Tech Zone Electronics',
      amount: 15000,
      paymentMethod: 'Cash',
      referenceNumber: '',
      collectionType: 'Outstanding Payment',
      collectedBy: 'Vikram Singh',
      commission: 375,
      status: 'Completed'
    },
    {
      id: 'COL004',
      date: '2024-01-12',
      fosId: 'FOS003',
      fosName: 'Rohan Mehta',
      retailerId: 'RET004',
      retailerName: 'Mobile Hub',
      amount: 12000,
      paymentMethod: 'Cheque',
      referenceNumber: 'CHQ001',
      collectionType: 'Advance Payment',
      collectedBy: 'Rohan Mehta',
      commission: 264,
      status: 'Pending'
    },
    {
      id: 'COL005',
      date: '2024-01-11',
      fosId: 'FOS002',
      fosName: 'Anita Desai',
      retailerId: 'RET005',
      retailerName: 'Electronic Store',
      amount: 8000,
      paymentMethod: 'UPI',
      referenceNumber: 'TXN123458',
      collectionType: 'Outstanding Payment',
      collectedBy: 'Anita Desai',
      commission: 160,
      status: 'Completed'
    }
  ];

  const fosList = [
    { id: 'FOS001', name: 'Vikram Singh' },
    { id: 'FOS002', name: 'Anita Desai' },
    { id: 'FOS003', name: 'Rohan Mehta' }
  ];

  const retailersList = [
    { id: 'RET001', name: 'Smart Mobile Store' },
    { id: 'RET002', name: 'Tech Zone Electronics' },
    { id: 'RET003', name: 'Digital World' },
    { id: 'RET004', name: 'Mobile Hub' },
    { id: 'RET005', name: 'Electronic Store' }
  ];

  useEffect(() => {
    setCollections(mockCollections);
  }, []);

  const filteredData = useMemo(() => {
    let filtered = collections;

    // Date Range Filter
    if (filters.dateRange.start && filters.dateRange.end) {
      const startDate = new Date(filters.dateRange.start);
      const endDate = new Date(filters.dateRange.end);
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    // FOS Filter
    if (filters.fosFilter) {
      filtered = filtered.filter(item => item.fosId === filters.fosFilter);
    }

    // Retailer Filter
    if (filters.retailerFilter) {
      filtered = filtered.filter(item => item.retailerId === filters.retailerFilter);
    }

    // Payment Method Filter
    if (filters.paymentMethod) {
      filtered = filtered.filter(item => item.paymentMethod === filters.paymentMethod);
    }

    // Collection Type Filter
    if (filters.collectionType) {
      filtered = filtered.filter(item => item.collectionType === filters.collectionType);
    }

    // Amount Range Filter
    if (filters.amountRange.min) {
      filtered = filtered.filter(item => item.amount >= parseFloat(filters.amountRange.min));
    }
    if (filters.amountRange.max) {
      filtered = filtered.filter(item => item.amount <= parseFloat(filters.amountRange.max));
    }

    return filtered;
  }, [collections, filters]);

  const summaryData = useMemo(() => {
    const totalAmount = filteredData.reduce((sum, item) => sum + item.amount, 0);
    const totalCommission = filteredData.reduce((sum, item) => sum + item.commission, 0);
    const completedCollections = filteredData.filter(item => item.status === 'Completed');
    const pendingCollections = filteredData.filter(item => item.status === 'Pending');

    const fosSummary = fosList.map(fos => {
      const fosCollections = filteredData.filter(item => item.fosId === fos.id);
      const fosTotal = fosCollections.reduce((sum, item) => sum + item.amount, 0);
      const fosCommission = fosCollections.reduce((sum, item) => sum + item.commission, 0);
      return {
        ...fos,
        totalAmount: fosTotal,
        totalCommission: fosCommission,
        collectionCount: fosCollections.length
      };
    });

    const paymentMethodSummary = ['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'DD'].map(method => {
      const methodCollections = filteredData.filter(item => item.paymentMethod === method);
      const methodTotal = methodCollections.reduce((sum, item) => sum + item.amount, 0);
      return {
        method,
        totalAmount: methodTotal,
        count: methodCollections.length
      };
    });

    return {
      totalAmount,
      totalCommission,
      totalCollections: filteredData.length,
      completedAmount: completedCollections.reduce((sum, item) => sum + item.amount, 0),
      pendingAmount: pendingCollections.reduce((sum, item) => sum + item.amount, 0),
      fosSummary,
      paymentMethodSummary: paymentMethodSummary.filter(item => item.count > 0)
    };
  }, [filteredData, fosList]);

  const handleFilterChange = (category, field, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: typeof prev[category] === 'object' && !Array.isArray(prev[category])
        ? { ...prev[category], [field]: value }
        : value
    }));
  };

  const clearFilters = () => {
    setFilters({
      dateRange: {
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
      },
      fosFilter: '',
      retailerFilter: '',
      paymentMethod: '',
      collectionType: '',
      amountRange: { min: '', max: '' },
      reportType: 'detailed'
    });
  };

  const generateReport = () => {
    const report = {
      summary: summaryData,
      collections: filteredData,
      filters: filters,
      generatedAt: new Date().toISOString()
    };
    setReportData(report);
    setShowPreviewModal(true);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary Sheet
    const summarySheet = [
      ['Collection Report Summary'],
      ['Generated On:', new Date().toLocaleDateString()],
      ['Period:', `${filters.dateRange.start} to ${filters.dateRange.end}`],
      [''],
      ['Total Collections:', summaryData.totalCollections],
      ['Total Amount:', `₹${summaryData.totalAmount.toLocaleString()}`],
      ['Total Commission:', `₹${summaryData.totalCommission.toLocaleString()}`],
      ['Completed Amount:', `₹${summaryData.completedAmount.toLocaleString()}`],
      ['Pending Amount:', `₹${summaryData.pendingAmount.toLocaleString()}`],
      [''],
      ['FOS Performance:'],
      ['FOS Name', 'Collections', 'Amount', 'Commission'],
      ...summaryData.fosSummary.map(fos => [
        fos.name,
        fos.collectionCount,
        fos.totalAmount,
        fos.totalCommission
      ])
    ];

    const summaryWS = XLSX.utils.aoa_to_sheet(summarySheet);
    XLSX.utils.book_append_sheet(workbook, summaryWS, 'Summary');

    // Details Sheet
    const detailsWS = XLSX.utils.json_to_sheet(filteredData.map(item => ({
      'Collection ID': item.id,
      'Date': item.date,
      'FOS': item.fosName,
      'Retailer': item.retailerName,
      'Amount': item.amount,
      'Payment Method': item.paymentMethod,
      'Reference': item.referenceNumber,
      'Type': item.collectionType,
      'Commission': item.commission,
      'Status': item.status
    })));
    XLSX.utils.book_append_sheet(workbook, detailsWS, 'Collections');

    XLSX.writeFile(workbook, `collection_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Report exported successfully');
  };

  const getStatusBadge = (status) => {
    return status === 'Completed' ? 
      <Badge bg="success">Completed</Badge> : 
      <Badge bg="warning">Pending</Badge>;
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-chart-bar me-2"></i>
          Collection Reports
        </h5>
        <div className="d-flex gap-2">
          <Button variant="info" onClick={generateReport}>
            <i className="fas fa-eye me-2"></i>
            Preview Report
          </Button>
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-file-excel me-2"></i>
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="g-4 mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-primary bg-opacity-10 rounded-circle me-3">
                  <i className="fas fa-coins text-primary"></i>
                </div>
                <div>
                  <h6 className="mb-1">Total Amount</h6>
                  <h4 className="mb-0 text-primary">₹{summaryData.totalAmount.toLocaleString()}</h4>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-success bg-opacity-10 rounded-circle me-3">
                  <i className="fas fa-percentage text-success"></i>
                </div>
                <div>
                  <h6 className="mb-1">Total Commission</h6>
                  <h4 className="mb-0 text-success">₹{summaryData.totalCommission.toLocaleString()}</h4>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-info bg-opacity-10 rounded-circle me-3">
                  <i className="fas fa-list text-info"></i>
                </div>
                <div>
                  <h6 className="mb-1">Total Collections</h6>
                  <h4 className="mb-0 text-info">{summaryData.totalCollections}</h4>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="p-3 bg-warning bg-opacity-10 rounded-circle me-3">
                  <i className="fas fa-clock text-warning"></i>
                </div>
                <div>
                  <h6 className="mb-1">Pending Amount</h6>
                  <h4 className="mb-0 text-warning">₹{summaryData.pendingAmount.toLocaleString()}</h4>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Advanced Filters */}
      <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
        <Card.Body>
          <h5 className="mb-3" style={{ color: '#031a6f' }}>
            <i className="fas fa-filter me-2"></i>
            Advanced Filters
          </h5>
          <Row>
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Date Range</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => handleFilterChange('dateRange', 'start', e.target.value)}
                  />
                  <Form.Control
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => handleFilterChange('dateRange', 'end', e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3">
              <Form.Group>
                <Form.Label>FOS</Form.Label>
                <Form.Select
                  value={filters.fosFilter}
                  onChange={(e) => handleFilterChange('fosFilter', null, e.target.value)}
                >
                  <option value="">All FOS</option>
                  {fosList.map(fos => (
                    <option key={fos.id} value={fos.id}>{fos.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3">
              <Form.Group>
                <Form.Label>Retailer</Form.Label>
                <Form.Select
                  value={filters.retailerFilter}
                  onChange={(e) => handleFilterChange('retailerFilter', null, e.target.value)}
                >
                  <option value="">All Retailers</option>
                  {retailersList.map(retailer => (
                    <option key={retailer.id} value={retailer.id}>{retailer.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3">
              <Form.Group>
                <Form.Label>Payment Method</Form.Label>
                <Form.Select
                  value={filters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', null, e.target.value)}
                >
                  <option value="">All Methods</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="DD">Demand Draft</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3">
              <Form.Group>
                <Form.Label>Collection Type</Form.Label>
                <Form.Select
                  value={filters.collectionType}
                  onChange={(e) => handleFilterChange('collectionType', null, e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="Outstanding Payment">Outstanding Payment</option>
                  <option value="Advance Payment">Advance Payment</option>
                  <option value="Regular Payment">Regular Payment</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={1} className="mb-3 d-flex align-items-end">
              <Button variant="outline-secondary" onClick={clearFilters} className="w-100">
                Clear
              </Button>
            </Col>
          </Row>
          <Row>
            <Col md={3} className="mb-3">
              <Form.Group>
                <Form.Label>Amount Range</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="number"
                    placeholder="Min"
                    value={filters.amountRange.min}
                    onChange={(e) => handleFilterChange('amountRange', 'min', e.target.value)}
                  />
                  <Form.Control
                    type="number"
                    placeholder="Max"
                    value={filters.amountRange.max}
                    onChange={(e) => handleFilterChange('amountRange', 'max', e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={2} className="mb-3">
              <Form.Group>
                <Form.Label>Report Type</Form.Label>
                <Form.Select
                  value={filters.reportType}
                  onChange={(e) => handleFilterChange('reportType', null, e.target.value)}
                >
                  <option value="detailed">Detailed</option>
                  <option value="summary">Summary Only</option>
                  <option value="fos_wise">FOS Wise</option>
                  <option value="payment_wise">Payment Method Wise</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Collections Table */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
        <Card.Body>
          <h5 className="mb-3" style={{ color: '#031a6f' }}>
            <i className="fas fa-table me-2"></i>
            Collections Data ({filteredData.length} records)
          </h5>
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Collection ID</th>
                  <th>FOS</th>
                  <th>Retailer</th>
                  <th>Amount</th>
                  <th>Method</th>
                  <th>Commission</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((collection) => (
                  <tr key={collection.id}>
                    <td>{collection.date}</td>
                    <td><strong>{collection.id}</strong></td>
                    <td>{collection.fosName}</td>
                    <td>{collection.retailerName}</td>
                    <td className="text-primary fw-bold">₹{collection.amount.toLocaleString()}</td>
                    <td>{collection.paymentMethod}</td>
                    <td className="text-success">₹{collection.commission.toLocaleString()}</td>
                    <td>{getStatusBadge(collection.status)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Report Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Collection Report Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportData && (
            <div>
              <div className="text-center mb-4">
                <h4>Collection Report</h4>
                <p className="text-muted">
                  Period: {filters.dateRange.start} to {filters.dateRange.end}
                </p>
              </div>

              <Row className="mb-4">
                <Col md={6}>
                  <h6>Summary</h6>
                  <ul className="list-unstyled">
                    <li>Total Collections: <strong>{reportData.summary.totalCollections}</strong></li>
                    <li>Total Amount: <strong>₹{reportData.summary.totalAmount.toLocaleString()}</strong></li>
                    <li>Total Commission: <strong>₹{reportData.summary.totalCommission.toLocaleString()}</strong></li>
                    <li>Completed: <strong>₹{reportData.summary.completedAmount.toLocaleString()}</strong></li>
                    <li>Pending: <strong>₹{reportData.summary.pendingAmount.toLocaleString()}</strong></li>
                  </ul>
                </Col>
                <Col md={6}>
                  <h6>FOS Performance</h6>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>FOS</th>
                        <th>Collections</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.summary.fosSummary
                        .filter(fos => fos.collectionCount > 0)
                        .map((fos, index) => (
                        <tr key={index}>
                          <td>{fos.name}</td>
                          <td>{fos.collectionCount}</td>
                          <td>₹{fos.totalAmount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-download me-2"></i>
            Export to Excel
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CollectionReports;